import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectLabel, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CUSTOM_MARKER = "__custom__";

export default function QuickActivityDialog({ open, onOpenChange, onCreated }) {
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clientId, setClientId] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  // Each line: {template_id, custom_title, quantity}
  const [lines, setLines] = useState([{ template_id: "", custom_title: "", quantity: 1 }]);
  const [saving, setSaving] = useState(false);
  const assigneeTriggerRef = useRef(null);

  const load = async () => {
    const [c, u, tpl] = await Promise.all([
      api.get("/clients"),
      api.get("/users"),
      api.get("/task-templates"),
    ]);
    setClients(c.data);
    setMembers(u.data);
    setTemplates(tpl.data || []);
  };

  useEffect(() => { if (open) load(); }, [open]);

  const reset = () => {
    setClientId(""); setAssigneeId(""); setPriority("medium"); setDueDate("");
    setLines([{ template_id: "", custom_title: "", quantity: 1 }]);
  };

  const templatesByCategory = useMemo(() => {
    const g = {};
    for (const t of templates) {
      const cat = t.category || "Other";
      (g[cat] ||= []).push(t);
    }
    // sort categories alphabetically, templates alphabetically within
    return Object.fromEntries(
      Object.entries(g)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, v.sort((a, b) => a.name.localeCompare(b.name))])
    );
  }, [templates]);

  const templateById = useMemo(() => Object.fromEntries(templates.map(t => [t.id, t])), [templates]);
  const clientLabel = (c) => c.company || c.name || "—";

  const addLine = () => setLines([...lines, { template_id: "", custom_title: "", quantity: 1 }]);
  const removeLine = (i) => setLines(lines.length === 1 ? lines : lines.filter((_, idx) => idx !== i));
  const setLine = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const lineTitle = (l) => {
    if (l.template_id === CUSTOM_MARKER) return l.custom_title.trim();
    const t = templateById[l.template_id];
    return t ? t.name : "";
  };
  const lineBenchmark = (l) => {
    if (l.template_id && l.template_id !== CUSTOM_MARKER) {
      const t = templateById[l.template_id];
      if (t) return Number(t.benchmark_per_unit) || 0;
    }
    return 0;
  };

  const validLines = lines.filter(l => lineTitle(l).length > 0);
  const canSubmit = !!clientId && validLines.length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await Promise.all(validLines.map(l => api.post("/tasks", {
        client_id: clientId,
        title: lineTitle(l),
        assignee_id: assigneeId || null,
        priority,
        due_date: dueDate || null,
        quantity: parseInt(l.quantity, 10) || 1,
        benchmark_per_unit: lineBenchmark(l),
        status: "todo",
      })));
      toast.success(`Created ${validLines.length} activit${validLines.length === 1 ? "y" : "ies"}`);
      reset();
      onOpenChange(false);
      if (onCreated) onCreated(clientId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg rounded-sm" data-testid="quick-activity-dialog">
        <DialogHeader>
          <DialogTitle>New activity</DialogTitle>
          <DialogDescription>
            Pick a client, assign it, and queue up benchmarked activities from your template library.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client</Label>
              <Select value={clientId} onValueChange={(v) => {
                setClientId(v);
                setTimeout(() => assigneeTriggerRef.current?.focus(), 60);
              }}>
                <SelectTrigger data-testid="qa-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{clientLabel(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assignee</Label>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger ref={assigneeTriggerRef} data-testid="qa-assignee-select"><SelectValue placeholder="Anyone" /></SelectTrigger>
                <SelectContent>
                  {members.map(m => <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="qa-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due date</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="qa-due-date" />
            </div>
          </div>

          <div className="border-t border-neutral-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">
                Activities ({lines.length})
              </div>
              <div className="flex items-center gap-2">
                <Link to="/settings/benchmarks" className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-neutral-900 hover:underline">
                  Manage templates →
                </Link>
                <Button variant="outline" size="sm" onClick={addLine} className="h-7 rounded-sm text-xs" data-testid="qa-add-line">
                  <Plus size={12} className="mr-1" /> Add line
                </Button>
              </div>
            </div>

            {templates.length === 0 && (
              <div className="mb-3 border border-amber-200 bg-amber-50 text-amber-900 text-[11px] px-3 py-2 rounded-sm">
                No task templates yet. <Link to="/settings/benchmarks" className="underline font-semibold">Create your first benchmark →</Link>
                <br />You can still type a custom activity title below for one-off work.
              </div>
            )}

            <div className="space-y-2 max-h-72 overflow-y-auto">
              {lines.map((l, i) => {
                const bench = lineBenchmark(l);
                return (
                  <div key={i} className="space-y-1" data-testid={`qa-line-${i}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <Select value={l.template_id} onValueChange={(v) => setLine(i, { template_id: v, custom_title: v === CUSTOM_MARKER ? l.custom_title : "" })}>
                          <SelectTrigger className="rounded-sm h-9" data-testid={`qa-template-${i}`}>
                            <SelectValue placeholder="Select task template…" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(templatesByCategory).map(([cat, list]) => (
                              <SelectGroup key={cat}>
                                <SelectLabel className="text-[10px] uppercase tracking-widest text-neutral-500">{cat}</SelectLabel>
                                {list.map(t => (
                                  <SelectItem key={t.id} value={t.id}>
                                    {t.name} · <span className="text-neutral-500 font-mono text-xs">{t.benchmark_per_unit}h</span>
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                            <SelectGroup>
                              <SelectLabel className="text-[10px] uppercase tracking-widest text-neutral-500">One-off</SelectLabel>
                              <SelectItem value={CUSTOM_MARKER}>✏️ Custom activity (type below)</SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                      <Input
                        type="number" min="1" placeholder="Qty"
                        value={l.quantity}
                        onChange={e => setLine(i, { quantity: e.target.value })}
                        className="w-16 rounded-sm font-mono text-xs h-9"
                        data-testid={`qa-qty-${i}`}
                        title="Quantity"
                      />
                      <Button variant="ghost" size="sm" onClick={() => removeLine(i)}
                              disabled={lines.length === 1}
                              className="h-9 w-9 p-0 rounded-sm text-neutral-400 hover:text-rose-600 disabled:opacity-30"
                              data-testid={`qa-remove-${i}`}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                    {l.template_id === CUSTOM_MARKER && (
                      <Input
                        placeholder="Custom activity title"
                        value={l.custom_title}
                        onChange={e => setLine(i, { custom_title: e.target.value })}
                        className="rounded-sm h-8"
                        data-testid={`qa-custom-title-${i}`}
                      />
                    )}
                    {bench > 0 && (
                      <div className="text-[10px] font-mono text-neutral-500 pl-1">
                        Benchmark: {(bench * (parseInt(l.quantity, 10) || 1)).toFixed(1)}h target ({bench}h × {l.quantity})
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}
                  className="rounded-sm" data-testid="qa-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || !canSubmit}
                  className="rounded-sm bg-neutral-900 hover:bg-neutral-800"
                  data-testid="qa-submit">
            {saving ? "Creating…" : `Create ${validLines.length || ""}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
